import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import Carousel from '../components/Carousel';
import Base64Image from '../components/Base64Image';
import {
  HomePageContainer,
  Section,
  SectionTitle,
  SectionBody,
  HeroSection,
  HeroInner,
  HeroBackground,
  HeroTextBlock,
  HeroTitle,
  HeroSubtitle,
  HeroBody,
  ProgramsGrid,
  ProgramCard,
  ProgramTitle,
  ProgramDescription,
  TestimoniesList,
  TestimonyCard,
  TestimonyTitle,
  TestimonyMeta,
  StatsRow,
  StatItem,
  StatNumber,
  StatLabel,
  AboutTwoColumn,
  AboutTextBlock,
  AboutImageBlock,
  HomeImageFrame,
  ActivitiesGrid,
  ActivitiesColumn,
  ActivitiesColumnTitle,
  ActivitiesCard,
  ActivitiesItemTitle,
  ActivitiesItemMeta,
  ActivitiesItemDescription,
} from '../styles/HomePageStyles';

const HOME_CACHE_KEY = 'home_data';
const TESTIMONIES_CACHE_KEY = 'testimonies_data';
const ABOUT_CACHE_KEY = 'about_data';
const ACTIVITIES_CACHE_KEY = 'activities_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const truncateText = (text, maxLength = 450) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
};

const extractAboutSummary = (data) => {
  if (!data) return null;

  const organizationBio = data.organizationBio || null;
  const text =
    (organizationBio && organizationBio.text) ||
    data.mission ||
    (data.history && data.history.text) ||
    null;

  const stats = data.growthStatistics || null;
  const image = organizationBio && organizationBio.image ? organizationBio.image : null;

  if (!text && !stats && !image) return null;

  return { text, stats, image };
};

const sortActivities = (list) => {
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => {
    const ao = typeof a.displayOrder === 'number' ? a.displayOrder : 0;
    const bo = typeof b.displayOrder === 'number' ? b.displayOrder : 0;
    return ao - bo;
  });
};

const HomePage = () => {
  const [homeData, setHomeData] = useState(null);
  const [featuredTestimonies, setFeaturedTestimonies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aboutSummary, setAboutSummary] = useState(null);
  const [academySummary, setAcademySummary] = useState(null);
  const [clinicSummary, setClinicSummary] = useState(null);
  const [rehabSummary, setRehabSummary] = useState(null);
   const [activitiesPage, setActivitiesPage] = useState(null);
   const [activitiesLoading, setActivitiesLoading] = useState(true);
   const [activitiesError, setActivitiesError] = useState(null);

  useEffect(() => {
    const cached = getCachedData(HOME_CACHE_KEY);

    if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
      setHomeData(cached.data);
      setLoading(false);
    }

    let isMounted = true;

    const fetchData = async () => {
      try {
        const data = await get('/home');
        if (!isMounted) return;
        setHomeData(data);
        setCachedData(HOME_CACHE_KEY, data);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setLoading(false);
        }
        setError(err && err.message ? err.message : 'Failed to load home page data.');
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const cached = getCachedData(ABOUT_CACHE_KEY);
    if (cached && !isCacheStale(cached, MAX_AGE_MS) && cached.data) {
      const summary = extractAboutSummary(cached.data);
      if (summary && isMounted) {
        setAboutSummary(summary);
      }
    }

    const fetchAbout = async () => {
      try {
        const data = await get('/about');
        if (!isMounted) return;
        const summary = extractAboutSummary(data);
        if (summary) {
          setAboutSummary(summary);
        }
        setCachedData(ABOUT_CACHE_KEY, data);
      } catch (err) {
        if (!isMounted) return;
        // Home page can still render without about snippet.
      }
    };

    fetchAbout();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadAcademySummary = async () => {
      try {
        const cached = await getCachedData('academy_data');
        if (cached && !isCacheStale(cached, MAX_AGE_MS) && cached.data && isMounted) {
          const page = cached.data;
          const headerData = page.header || {};
          const establishmentStory = page.establishmentStory || {};
          const summary = {
            title: headerData.title || 'Victorious Faith Academy / Refuge Home',
            text: establishmentStory.text || null,
            image: establishmentStory.image || null,
          };
          if (summary.text || summary.image) {
            setAcademySummary(summary);
          }
        }
      } catch (err) {}

      try {
        const data = await get('/academy');
        if (!isMounted) return;
        setCachedData('academy_data', data);
        const headerData = data.header || {};
        const establishmentStory = data.establishmentStory || {};
        const summary = {
          title: headerData.title || 'Victorious Faith Academy / Refuge Home',
          text: establishmentStory.text || null,
          image: establishmentStory.image || null,
        };
        if (summary.text || summary.image) {
          setAcademySummary(summary);
        }
      } catch (err) {}
    };

    loadAcademySummary();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadClinicSummary = async () => {
      try {
        const cached = await getCachedData('clinic_data');
        if (cached && !isCacheStale(cached, MAX_AGE_MS) && cached.data && isMounted) {
          const page = cached.data;
          const headerData = page.header || {};
          const establishmentStory = page.establishmentStory || {};
          const summary = {
            title: headerData.title || 'Community Clinic',
            text: page.overviewText || null,
            image: establishmentStory.image || null,
          };
          if (summary.text || summary.image) {
            setClinicSummary(summary);
          }
        }
      } catch (err) {}

      try {
        const data = await get('/clinic');
        if (!isMounted) return;
        setCachedData('clinic_data', data);
        const headerData = data.header || {};
        const establishmentStory = data.establishmentStory || {};
        const summary = {
          title: headerData.title || 'Community Clinic',
          text: data.overviewText || null,
          image: establishmentStory.image || null,
        };
        if (summary.text || summary.image) {
          setClinicSummary(summary);
        }
      } catch (err) {}
    };

    loadClinicSummary();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadRehabSummary = async () => {
      try {
        const cached = await getCachedData('rehab_data');
        if (cached && !isCacheStale(cached, MAX_AGE_MS) && cached.data && isMounted) {
          const page = cached.data;
          const headerData = page.header || {};
          const summary = {
            title: headerData.title || 'Addict to Disciple Program',
            text: page.overviewText || null,
            image: headerData.backgroundImage || null,
          };
          if (summary.text || summary.image) {
            setRehabSummary(summary);
          }
        }
      } catch (err) {}

      try {
        const data = await get('/rehab');
        if (!isMounted) return;
        setCachedData('rehab_data', data);
        const headerData = data.header || {};
        const summary = {
          title: headerData.title || 'Addict to Disciple Program',
          text: data.overviewText || null,
          image: headerData.backgroundImage || null,
        };
        if (summary.text || summary.image) {
          setRehabSummary(summary);
        }
      } catch (err) {}
    };

    loadRehabSummary();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const cached = getCachedData(TESTIMONIES_CACHE_KEY);

    if (cached && !isCacheStale(cached, MAX_AGE_MS) && Array.isArray(cached.data)) {
      const featured = cached.data.filter((post) => post.isFeatured && post.status === 'published');
      setFeaturedTestimonies(featured);
    }

    const fetchTestimonies = async () => {
      try {
        const posts = await get('/testimonies/posts?status=published');
        if (!isMounted) return;
        setCachedData(TESTIMONIES_CACHE_KEY, posts);
        if (Array.isArray(posts)) {
          const featured = posts.filter((post) => post.isFeatured);
          setFeaturedTestimonies(featured);
        }
      } catch (err) {
        if (!isMounted) return;
      }
    };

    fetchTestimonies();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const cached = getCachedData(ACTIVITIES_CACHE_KEY);

    if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
      setActivitiesPage(cached.data);
      setActivitiesLoading(false);
    }

    const fetchActivities = async () => {
      try {
        const data = await get('/activities');
        if (!isMounted) return;
        setActivitiesPage(data);
        setCachedData(ACTIVITIES_CACHE_KEY, data);
        setActivitiesLoading(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setActivitiesLoading(false);
        }
        setActivitiesError(
          err && err.message ? err.message : 'Failed to load activities data.',
        );
      }
    };

    fetchActivities();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading && !homeData) {
    return (
      <HomePageContainer>
        <LoadingSpinner />
      </HomePageContainer>
    );
  }

  if (error && !homeData) {
    return <HomePageContainer>{error}</HomePageContainer>;
  }

  const header = homeData && homeData.header ? homeData.header : null;
  const heroMessages = homeData && Array.isArray(homeData.heroMessages) ? homeData.heroMessages : [];
  const heroImages = homeData && Array.isArray(homeData.heroImages) ? homeData.heroImages : [];
  const mottoText = homeData ? homeData.mottoText : null;
  const featuredActivities = homeData ? homeData.featuredActivities : null;
  const featuredNewsSnippet = homeData ? homeData.featuredNewsSnippet : null;
  const featuredPrograms = homeData && Array.isArray(homeData.featuredPrograms)
    ? homeData.featuredPrograms
    : [];
  const contactSection = homeData ? homeData.contactSection : null;

  const aboutText = aboutSummary && aboutSummary.text ? aboutSummary.text : null;
  const aboutStats = aboutSummary && aboutSummary.stats ? aboutSummary.stats : null;
  const aboutImage = aboutSummary && aboutSummary.image ? aboutSummary.image : null;

  const weeklyActivities = sortActivities(activitiesPage && activitiesPage.weeklyActivities);
  const monthlyActivities = sortActivities(activitiesPage && activitiesPage.monthlyActivities);
  const yearlyActivities = sortActivities(activitiesPage && activitiesPage.yearlyActivities);

  const hasAnyActivities =
    (weeklyActivities && weeklyActivities.length > 0) ||
    (monthlyActivities && monthlyActivities.length > 0) ||
    (yearlyActivities && yearlyActivities.length > 0);

  const programsByKey = {};
  featuredPrograms.forEach((program) => {
    if (program && program.key) {
      programsByKey[program.key] = program;
    }
  });

  const academyProgram = programsByKey.academy;
  const clinicProgram = programsByKey.clinic;
  const rehabProgram = programsByKey.rehab;

  const academySummaryText =
    academySummary && academySummary.text ? truncateText(academySummary.text) : null;
  const clinicSummaryText =
    clinicSummary && clinicSummary.text ? truncateText(clinicSummary.text) : null;
  const rehabSummaryText =
    rehabSummary && rehabSummary.text ? truncateText(rehabSummary.text) : null;

  const renderActivitiesColumn = (title, list) => {
    if (!list || list.length === 0) return null;
    return (
      <ActivitiesColumn>
        <ActivitiesColumnTitle>{title}</ActivitiesColumnTitle>
        {list.map((item, index) => (
          <ActivitiesCard
            key={`${item.title || item.day || 'activity'}-${index}`}
          >
            {(item.title || item.day) && (
              <ActivitiesItemTitle>{item.title || item.day}</ActivitiesItemTitle>
            )}
            {(item.day || item.time) && (
              <ActivitiesItemMeta>
                {item.day && <span>{item.day}</span>}
                {item.day && item.time && <span>  b7 </span>}
                {item.time && <span>{item.time}</span>}
              </ActivitiesItemMeta>
            )}
            {item.description && (
              <ActivitiesItemDescription>{item.description}</ActivitiesItemDescription>
            )}
          </ActivitiesCard>
        ))}
      </ActivitiesColumn>
    );
  };

  return (
    <HomePageContainer>
      <HeroSection>
        {heroImages.length > 0 && (
          <HeroBackground>
            <Carousel
              items={heroImages}
              renderItem={(image) => (
                <Base64Image
                  image={image}
                  alt={header && header.organizationName ? header.organizationName : 'Hero'}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              )}
              intervalMs={6000}
            />
          </HeroBackground>
        )}
        <HeroInner>
          <HeroTextBlock>
            {heroMessages.length > 0 ? (
              <Carousel
                items={heroMessages}
                renderItem={(item) => (
                  <>
                    {item.title && <HeroTitle>{item.title}</HeroTitle>}
                    {item.subtitle && <HeroSubtitle>{item.subtitle}</HeroSubtitle>}
                    {item.body && <HeroBody>{item.body}</HeroBody>}
                  </>
                )}
                intervalMs={7000}
              />
            ) : (
              <>
                {header && header.organizationName && (
                  <HeroTitle>{header.organizationName}</HeroTitle>
                )}
                {mottoText && <HeroSubtitle>{mottoText}</HeroSubtitle>}
              </>
            )}
          </HeroTextBlock>
        </HeroInner>
      </HeroSection>

      <Section>
        {header && header.organizationName && (
          <SectionTitle>{header.organizationName}</SectionTitle>
        )}
        <SectionBody>
          {mottoText && <p>{mottoText}</p>}
          {aboutText && (
            <AboutTwoColumn>
              <AboutTextBlock>
                {aboutText}
                <div style={{ marginTop: 12 }}>
                  <Link to="/about">Learn more about us</Link>
                </div>
              </AboutTextBlock>
              {aboutImage && (
                <AboutImageBlock>
                  <HomeImageFrame>
                    <Base64Image
                      image={aboutImage}
                      alt={
                        header && header.organizationName
                          ? header.organizationName
                          : 'About us'
                      }
                    />
                  </HomeImageFrame>
                </AboutImageBlock>
              )}
            </AboutTwoColumn>
          )}
          {aboutStats && (
            <StatsRow>
              {typeof aboutStats.membersCount === 'number' && (
                <StatItem>
                  <StatNumber>{aboutStats.membersCount}+</StatNumber>
                  <StatLabel>Members</StatLabel>
                </StatItem>
              )}
              {typeof aboutStats.branchesCount === 'number' && (
                <StatItem>
                  <StatNumber>{aboutStats.branchesCount}</StatNumber>
                  <StatLabel>Branches</StatLabel>
                </StatItem>
              )}
              {typeof aboutStats.internationalBranchesCount === 'number' && (
                <StatItem>
                  <StatNumber>{aboutStats.internationalBranchesCount}</StatNumber>
                  <StatLabel>International Branches</StatLabel>
                </StatItem>
              )}
              {typeof aboutStats.foundedYear === 'number' && (
                <StatItem>
                  <StatNumber>{aboutStats.foundedYear}</StatNumber>
                  <StatLabel>Founded</StatLabel>
                </StatItem>
              )}
            </StatsRow>
          )}
        </SectionBody>
      </Section>

      {(hasAnyActivities || featuredActivities || activitiesError) && (
        <Section>
          <SectionTitle>Church Activities</SectionTitle>
          <SectionBody>
            {featuredActivities && (
              <>
                {featuredActivities.weeklySnippet && <p>{featuredActivities.weeklySnippet}</p>}
                {featuredActivities.monthlySnippet && <p>{featuredActivities.monthlySnippet}</p>}
                {featuredActivities.yearlySnippet && <p>{featuredActivities.yearlySnippet}</p>}
              </>
            )}

            {activitiesError && !hasAnyActivities && (
              <p style={{ color: '#b71c1c' }}>{activitiesError}</p>
            )}

            {hasAnyActivities && !activitiesLoading && (
              <>
                <ActivitiesGrid>
                  {renderActivitiesColumn('Weekly Activities', weeklyActivities)}
                  {renderActivitiesColumn('Monthly Activities', monthlyActivities)}
                  {renderActivitiesColumn('Yearly Activities', yearlyActivities)}
                </ActivitiesGrid>
                <div style={{ marginTop: 12 }}>
                  <Link to="/churchactivities">View full activities schedule</Link>
                </div>
              </>
            )}
          </SectionBody>
        </Section>
      )}

      {featuredNewsSnippet && (
        <Section>
          {featuredNewsSnippet.title && (
            <SectionTitle>
              <Link to="/churchactivities">{featuredNewsSnippet.title}</Link>
            </SectionTitle>
          )}
          <SectionBody>
            {featuredNewsSnippet.summary && <p>{featuredNewsSnippet.summary}</p>}
          </SectionBody>
        </Section>
      )}

      {(academySummary || academyProgram) && (
        <Section>
          <SectionTitle>
            <Link to="/school">
              {(academySummary && academySummary.title) ||
                (academyProgram && academyProgram.title) ||
                'Victorious Faith Academy / Refuge Home'}
            </Link>
          </SectionTitle>
          <SectionBody>
            {academySummary ? (
              <AboutTwoColumn>
                <AboutTextBlock>
                  {academySummaryText && <p>{academySummaryText}</p>}
                </AboutTextBlock>
                {academySummary.image && (
                  <AboutImageBlock>
                    <HomeImageFrame>
                      <Base64Image
                        image={academySummary.image}
                        alt={academySummary.title || 'Academy / Refuge Home'}
                      />
                    </HomeImageFrame>
                  </AboutImageBlock>
                )}
              </AboutTwoColumn>
            ) : (
              <>
                {academyProgram && academyProgram.image && (
                  <div style={{ marginBottom: 12 }}>
                    <HomeImageFrame>
                      <Base64Image
                        image={academyProgram.image}
                        alt={academyProgram.title || 'Academy / Refuge Home'}
                      />
                    </HomeImageFrame>
                  </div>
                )}
                {academyProgram && academyProgram.description && <p>{academyProgram.description}</p>}
              </>
            )}
          </SectionBody>
        </Section>
      )}

      {(clinicSummary || clinicProgram) && (
        <Section>
          <SectionTitle>
            <Link to="/clinic">
              {(clinicSummary && clinicSummary.title) ||
                (clinicProgram && clinicProgram.title) ||
                'The Victorious Faith Community Clinic'}
            </Link>
          </SectionTitle>
          <SectionBody>
            {clinicSummary ? (
              <AboutTwoColumn>
                {clinicSummary.image && (
                  <AboutImageBlock>
                    <HomeImageFrame>
                      <Base64Image
                        image={clinicSummary.image}
                        alt={clinicSummary.title || 'Community Clinic'}
                      />
                    </HomeImageFrame>
                  </AboutImageBlock>
                )}
                <AboutTextBlock>
                  {clinicSummaryText && <p>{clinicSummaryText}</p>}
                </AboutTextBlock>
              </AboutTwoColumn>
            ) : (
              <>
                {clinicProgram && clinicProgram.image && (
                  <div style={{ marginBottom: 12 }}>
                    <HomeImageFrame>
                      <Base64Image
                        image={clinicProgram.image}
                        alt={clinicProgram.title || 'Community Clinic'}
                      />
                    </HomeImageFrame>
                  </div>
                )}
                {clinicProgram && clinicProgram.description && <p>{clinicProgram.description}</p>}
              </>
            )}
          </SectionBody>
        </Section>
      )}

      {(rehabSummary || rehabProgram) && (
        <Section>
          <SectionTitle>
            <Link to="/rehab">
              {(rehabSummary && rehabSummary.title) ||
                (rehabProgram && rehabProgram.title) ||
                'Addict to Disciple Program'}
            </Link>
          </SectionTitle>
          <SectionBody>
            {rehabSummary ? (
              <AboutTwoColumn>
                <AboutTextBlock>
                  {rehabSummaryText && <p>{rehabSummaryText}</p>}
                </AboutTextBlock>
                {rehabSummary.image && (
                  <AboutImageBlock>
                    <HomeImageFrame>
                      <Base64Image
                        image={rehabSummary.image}
                        alt={rehabSummary.title || 'Rehab Program'}
                      />
                    </HomeImageFrame>
                  </AboutImageBlock>
                )}
              </AboutTwoColumn>
            ) : (
              <>
                {rehabProgram && rehabProgram.image && (
                  <div style={{ marginBottom: 12 }}>
                    <HomeImageFrame>
                      <Base64Image
                        image={rehabProgram.image}
                        alt={rehabProgram.title || 'Rehab Program'}
                      />
                    </HomeImageFrame>
                  </div>
                )}
                {rehabProgram && rehabProgram.description && <p>{rehabProgram.description}</p>}
              </>
            )}
          </SectionBody>
        </Section>
      )}

      {featuredTestimonies && featuredTestimonies.length > 0 && (
        <Section>
          <SectionTitle>Featured Testimonies</SectionTitle>
          <SectionBody>
            <TestimoniesList>
              {featuredTestimonies.map((post) => (
                <TestimonyCard key={post._id}>
                  {post.title && <TestimonyTitle>{post.title}</TestimonyTitle>}
                  <TestimonyMeta>
                    {post.author && <span>{post.author}</span>}
                    {post.date && (
                      <span>
                        {post.author ? ' • ' : ''}
                        {new Date(post.date).toLocaleDateString()}
                      </span>
                    )}
                  </TestimonyMeta>
                  {post.description && <p>{post.description}</p>}
                </TestimonyCard>
              ))}
            </TestimoniesList>
            <div style={{ marginTop: 16 }}>
              <Link to="/testimonies">View all testimonies</Link>
            </div>
          </SectionBody>
        </Section>
      )}

      {contactSection && (
        <Section>
          <SectionTitle>Contact</SectionTitle>
          <SectionBody>
            {contactSection.phone && <p>{contactSection.phone}</p>}
            {contactSection.email && <p>{contactSection.email}</p>}
            {contactSection.location && <p>{contactSection.location}</p>}
            {contactSection.contactFormSnippet && <p>{contactSection.contactFormSnippet}</p>}
          </SectionBody>
        </Section>
      )}
    </HomePageContainer>
  );
};

export default HomePage;
